import Decimal from 'decimal.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LiFiClient from '../../clients/LiFiClient'
import { EarnPosition } from '../../interfaces/earn'
import { logger } from '../../logger'
import { getTokenBalance } from '../../utils/getTokenBalances'
import getTokenMetadata from '../../utils/tokenActions/getMetadata'
import useConfig from '../useConfig'
import useWallet from '../useWallet'

export type WithdrawState = 'idle' | 'loading' | 'success' | 'error'

export const useEarnWithdraw = (position: EarnPosition) => {
  const { evmChains } = useConfig()
  const { wallet } = useWallet()
  const { t } = useTranslation()

  const [amount, setAmount] = useState('')
  const [withdrawState, setWithdrawState] = useState<WithdrawState>('idle')
  const [depositTokenBalance, setDepositTokenBalance] = useState<bigint>(0n)

  const balanceUsd = Number(position.balanceUsd ?? 0)
  const balanceNative = Number(position.balanceNative ?? 0)
  const numericAmount = Number(amount) || 0

  let receiveNative = 0
  if (balanceUsd > 0 && numericAmount > 0) {
    receiveNative = new Decimal(numericAmount).div(balanceUsd).mul(balanceNative).toNumber()
  }

  useEffect(() => {
    const evmChain = evmChains.find((c) => c.chainId === position.chainId)
    if (!evmChain) return

    getTokenMetadata(position.address, '', { evmChain })
      .then(async (token) => {
        if (token) {
          const balance = await getTokenBalance(wallet, token)
          setDepositTokenBalance(BigInt(balance))
        }
      })
      .catch((e) => logger.error('Failed to fetch deposit token metadata', { originalError: e }))
  }, [position.address, position.chainId, evmChains, wallet])

  const error = useMemo(() => {
    if (!numericAmount) {
      return t('swap.errors.no_amount')
    }

    if (numericAmount > balanceUsd) {
      return t('earn.withdraw.exceedsBalance')
    }

    if (depositTokenBalance <= 0n) {
      return t('earn.withdraw.alreadyWithdrawn')
    }
  }, [numericAmount, balanceUsd, t, depositTokenBalance])

  const executeWithdraw = useCallback(async () => {
    setWithdrawState('loading')

    try {
      const shareAmount = BigInt(
        new Decimal(numericAmount).div(balanceUsd).mul(depositTokenBalance.toString()).toFixed(0)
      )

      await new LiFiClient().withdraw(position, shareAmount, wallet, evmChains)

      setWithdrawState('success')
    } catch (e) {
      logger.error('Failed to execute withdraw', { originalError: e })
      setWithdrawState('error')
    }
  }, [numericAmount, balanceUsd, depositTokenBalance, position, wallet, evmChains])

  return {
    amount,
    setAmount,
    balanceUsd,
    receiveNative,
    assetSymbol: position.asset.symbol,
    error,
    withdrawState,
    executeWithdraw,
  }
}
